import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';
import { formatDateTime } from '@/shared/lib/format';
import { useAnnounceRelease, useAnnouncement } from './queries';
import type { Release } from './types';

/**
 * "NuDent x.y.z is available" with the release's title, summary and notes, to
 * every lab user who left "Version update available" on in the user portal.
 * Released stable releases only, once per version number.
 */
export function EmailLabsDialog({ release }: { release: Release }) {
  const [open, setOpen] = useState(false);
  const preview = useAnnouncement(release.id, open);
  const send = useAnnounceRelease(release.id);
  const data = preview.data;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Mail size={15} /> Email labs
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email labs about {release.versionNumber}</DialogTitle>
          <DialogDescription>
            &ldquo;NuDent {release.versionNumber} is available
            {release.title ? `: ${release.title}` : ''}&rdquo;, with its summary and release notes,
            to every lab user who has &ldquo;Version update available&rdquo; on.
          </DialogDescription>
        </DialogHeader>

        {preview.isPending && <Skeleton className="h-10 w-full" />}
        {preview.isError && <p className="text-sm text-destructive">{preview.error.message}</p>}
        {data &&
          (data.previous ? (
            <p className="text-sm">
              Already sent to {data.previous.count}{' '}
              {data.previous.count === 1 ? 'person' : 'people'} by {data.previous.sentBy} on{' '}
              {formatDateTime(data.previous.sentAt)}. It goes once per version.
            </p>
          ) : (
            <p className="text-sm">
              This reaches <strong>{data.recipients}</strong>{' '}
              {data.recipients === 1 ? 'person' : 'people'}.
              {!release.releaseNotes && ' It has no release notes - add them first.'}
            </p>
          ))}

        {send.isError && <p className="text-sm text-destructive">{send.error.message}</p>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!data || Boolean(data.previous) || data.recipients === 0 || send.isPending}
            onClick={() => {
              send.mutate(undefined, {
                onSuccess: () => {
                  setOpen(false);
                },
              });
            }}
          >
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
